/**
 * media-gallery.js
 *
 * A custom element <media-gallery> that:
 *  • Listens for slide‐change events on its “viewer” carousel
 *  • Switches the active thumbnail accordingly
 *  • Lets thumbnails drive the viewer when clicked
 *  • Announces the new image to a hidden live region for accessibility
 */

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      // 1. Cache references to the key sub‐elements (or null if missing):
      this.elements = {
        liveRegion:     this.querySelector('[id^="GalleryStatus"]')       || null,
        viewer:         this.querySelector('[id^="GalleryViewer"]')       || null,
        thumbnails:     this.querySelector('[id^="GalleryThumbnails"]')   || null
      };

      // 2. Media query for desktop layout:
      this.mql = window.matchMedia('(min-width: 750px)');

      // 3. (Optional) If there’s a ProductInfo section with share links:
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`) || null;
      if (this.productInfo && typeof Shopify?.postLinksRetry === 'function') {
        this.productInfo.initShareLinks();
      }

      // If there are no thumbnails, we skip setting up most of the gallery logic:
      if (!this.elements.thumbnails || !this.elements.viewer) {
        return;
      }

      // 4. Listen for slide‐changed on the “viewer” carousel (debounced to avoid thrash):
      //    NOTE: debounce(...) must already be defined on the page (e.g. via Shrine/Secondary.js).
      this.elements.viewer.addEventListener(
        'slideChanged',
        debounce(this.onSlideChanged.bind(this), 500)
      );

      // 5. Clicking on any thumbnail’s button should switch to that media ID:
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const button = mediaToSwitch.querySelector('button');
        if (!button) return;
        button.addEventListener(
          'click',
          () => this.setActiveMedia(mediaToSwitch.dataset.target, /* prepend=*/ false)
        );
      });

      // 6. If desktop layout uses “thumbnail” and we’re already on desktop, remove list semantics:
      if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
        this.removeListSemantic();
      }
    }

    /**
     * Called whenever the “viewer” carousel fires a slideChanged event.
     * We must guard against event.detail or event.detail.currentElement being undefined.
     */
    onSlideChanged(event) {
      // 1) If there is no detail or no currentElement, bail out immediately:
      if (!event?.detail || !event.detail.currentElement) return;

      // 2) Attempt to read the mediaId, bail if missing:
      const mediaId = event.detail.currentElement.dataset?.mediaId;
      if (!mediaId) return;

      // 3) If there is no thumbnails container, bail:
      if (!this.elements.thumbnails) return;

      // 4) Try to find the thumbnail that matches this mediaId:
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (!thumbnail) return;

      // 5) Highlight that thumbnail:
      this.setActiveThumbnail(thumbnail);
    }

    /**
     * Activate the media (by mediaId) inside the viewer. Optionally “prepend” it to the front.
     */
    setActiveMedia(mediaId, prepend) {
      if (!this.elements.viewer) return;

      // 1. Find the media element to activate:
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      // 2. Remove “is-active” from all media siblings, then add to this one:
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
        el.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      // 3. If “prepend” is requested, move this media (and its thumbnail) to the front:
      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

        // If the viewer has a slider API, reset pages so the “first” is visible:
        if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }
      }

      // 4. Prevent a sticky header from popping open right away:
      this.preventStickyHeader();

      // 5. After a brief timeout, scroll the media into view:
      window.setTimeout(() => {
        // If thumbnails exist, scroll the viewer’s parent horizontally so that activeMedia is visible:
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        // If no thumbnails, or desktopLayout is “stacked,” scroll it into view smoothly:
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);

      // 6. If this media has deferred content (e.g. a video or 3D model), load it now:
      this.playActiveMedia(activeMedia);

      // 7. If no thumbnails, no further work:
      if (!this.elements.thumbnails) return;

      // 8. Otherwise, ensure the correct thumbnail gets “aria-current” and scroll the thumbnail container:
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);

      // 9. Announce the new active slide for screen readers:
      const position = activeThumbnail?.dataset?.mediaPosition || '';
      this.announceLiveRegion(activeMedia, position);
    }

    /**
     * Mark one <thumbnail> as active (aria-current="true") and, if not in view, scroll it into view.
     */
    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      // 1. Clear aria-current from all thumbnail buttons:
      this.elements.thumbnails.querySelectorAll('button').forEach((btn) => {
        btn.removeAttribute('aria-current');
      });

      // 2. Set the clicked thumbnail’s button to aria-current="true":
      const btn = thumbnail.querySelector('button');
      if (btn) {
        btn.setAttribute('aria-current', 'true');
      }

      // 3. If the thumbnail is already visible in its slider, done:
      if (typeof this.elements.thumbnails.isSlideVisible === 'function') {
        if (this.elements.thumbnails.isSlideVisible(thumbnail, /* offset */ 10)) {
          return;
        }
      }

      // 4. Otherwise, scroll the thumbnail container so the thumbnail is revealed:
      if (this.elements.thumbnails.slider && typeof this.elements.thumbnails.slider.scrollTo === 'function') {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    /**
     * Announce the newly active media (for a11y). We update a hidden live region
     * with “Image X of Y is now available,” then hide it again after 2s.
     */
    announceLiveRegion(activeItem, position) {
      if (!this.elements.liveRegion) return;

      // 1. Try to find an <img> inside a “.product__modal-opener--image” wrapper:
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;

      // 2. When that image finishes loading, flip on the live region:
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', 'false');
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'true');
        }, 2000);
      };

      // 3. Force reload so onload fires even if it was cached:
      image.src = image.src;
    }

    /**
     * Pause any other media, then if activeItem has a “.deferred-media” child, load it.
     */
    playActiveMedia(activeItem) {
      if (typeof window.pauseAllMedia === 'function') {
        window.pauseAllMedia();
      }

      // If there’s a “.deferred-media” (video, 3D model, etc.), load it:
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent(false);
      }
    }

    /**
     * Prevent any “sticky-header” from unexpectedly popping into view
     * when the gallery changes. We dispatch a custom event “preventHeaderReveal.”
     */
    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    /**
     * If desktopLayout includes “thumbnail,” we remove list semantics so that
     * screen readers skip over slider role=”list” items.
     */
    removeListSemantic() {
      if (!this.elements.viewer || !this.elements.viewer.slider) return;

      // 1. Mark the slider container as presentation
      this.elements.viewer.slider.setAttribute('role', 'presentation');

      // 2. Mark each slider item as presentation
      if (Array.isArray(this.elements.viewer.sliderItems)) {
        this.elements.viewer.sliderItems.forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  });
}
